import { env } from '../../config/env';

const API_BASE_URL = env.apiBaseUrl;

async function requestAuth(endpoint, data) {
  const response = await fetch(`${API_BASE_URL}/auth/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload.message || 'Unable to complete request.');
  }

  return payload;
}

async function requestApi(endpoint, token, options = {}) {
  const { signal, ...rest } = options;
  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    ...rest,
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload.message || 'Unable to complete request.');
  }

  return payload;
}

export function loginUser(credentials) {
  return requestAuth('login', credentials);
}

export function signupUser(account) {
  return requestAuth('signup', account);
}

export function getAdmins(token, params = {}) {
  const query = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, value]) => value != null && value !== '')
    )
  ).toString();
  return requestApi(`admins${query ? `?${query}` : ''}`, token);
}

export function updateAdminStatus(token, adminId, isActive) {
  return requestApi(`admins/${adminId}/status`, token, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  });
}

export function getSuperAdminAnalytics(token, options = {}) {
  const params = new URLSearchParams();
  if (options.adminId) params.set('adminId', options.adminId);
  if (options.page) params.set('page', String(options.page));
  if (options.pageSize) params.set('pageSize', String(options.pageSize));
  if (options.days) params.set('days', String(options.days));
  const query = params.toString();
  return requestApi(`admins/analytics${query ? `?${query}` : ''}`, token);
}

export function getSuperAdminPersonalizedAnalytics(token, { adminId, days = 30 } = {}) {
  const params = new URLSearchParams({
    view: 'full',
    days: String(days),
  });
  if (adminId && adminId !== 'overall') {
    params.set('adminId', String(adminId));
  }
  return requestApi(`admins/analytics?${params.toString()}`, token);
}

export function saveSpApiCredentials(token, credentials) {
  return requestApi('admin/sp-api', token, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

export function renewSpApiCredentials(token, credentials) {
  return requestApi('admin/sp-api', token, {
    method: 'PUT',
    body: JSON.stringify(credentials),
  });
}

export function updateSpApiSellerId(token, sellerId) {
  return requestApi('admin/sp-api/seller-id', token, {
    method: 'PATCH',
    body: JSON.stringify({ sellerId }),
  });
}

export function getSpApiStatus(token) {
  return requestApi('admin/sp-api', token);
}

export function getCurrentSession(token, options = {}) {
  return requestApi('auth/me', token, { method: 'GET', ...options });
}
